import { User } from 'firebase/auth'
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore'
import { auth, db } from '../../firebase'
import {
  BaseUser,
  AccountStatus,
  ProfileCompletion,
} from '../../types/userTypes'
import { UserRoles } from '../../constants/userRoles'
import type {
  UserRole,
  AccountMode,
  PrivilegeType,
} from '../../constants/userRoles'
import {
  extractNameFromDisplayNameOrEmail,
  calculateProfileCompletion,
} from '../userDataValidation'

interface UserData extends Omit<User, 'phoneNumber'> {
  role?: UserRole
  privileges?: PrivilegeType[]
  corporateEntityId?: string | null
  corporateAccountId?: string | null
  accountMode?: AccountMode
  // Extended with BaseUser fields for backward compatibility
  firstName?: string
  lastName?: string
  phoneNumber?: string | null
  timezone?: string
  accountStatus?: AccountStatus
  profileComplete?: boolean
  profileCompletion?: ProfileCompletion | null
}

// Enhanced user document creation with Phase 1 fields
export const createEnhancedUserDocument = async (
  user: User,
  role: UserRole,
  names: { firstName: string; lastName: string },
  createdBy: string | null = null,
  additionalData?: Partial<BaseUser>
): Promise<BaseUser> => {
  const userRef = doc(db, 'users', user.uid)

  // Determine if this is a self-registered admin (Phase 1.5 - Simple Mode defaults)
  const isSelfRegisteredAdmin = role === UserRoles.ADMIN && createdBy === null

  // Calculate profile completion
  const baseUserData: BaseUser = {
    // Core Identity Fields
    uid: user.uid,
    email: user.email || '',
    displayName: user.displayName || `${names.firstName} ${names.lastName}`,
    photoURL: user.photoURL || '',

    // New Core Fields (Phase 1)
    firstName: names.firstName,
    lastName: names.lastName,
    phoneNumber: additionalData?.phoneNumber || '',
    ...(additionalData?.phoneNumber &&
      additionalData.phoneCountry && {
        phoneCountry: additionalData.phoneCountry,
      }),
    timezone: additionalData?.timezone || '',

    // System Fields
    role: role,
    accountStatus:
      (additionalData?.accountStatus as AccountStatus) || 'pending',
    emailVerified: user.emailVerified,

    // Organizational Fields
    // Self-registered admins become their own corporate account owner
    // Their UID serves as the corporateAccountId for their organization
    corporateAccountId: isSelfRegisteredAdmin
      ? user.uid
      : additionalData?.corporateAccountId || null,
    organizationId: additionalData?.organizationId || '',
    parentUserId: createdBy || null,
    // Invitation that admitted this user to the account (rules require it
    // when joining someone else's account — RS-2 step 5)
    ...(additionalData?.invitationId && {
      invitationId: additionalData.invitationId,
    }),

    // Permission & Privilege Fields
    privileges: additionalData?.privileges || [],
    hasAdminPrivileges: additionalData?.hasAdminPrivileges || false,

    // Simple Mode Plan Settings (Phase 1.5)
    // Self-registered admins default to Basic Plan with Simple Mode
    planTier: isSelfRegisteredAdmin
      ? 'basic'
      : additionalData?.planTier || null,
    accountMode: isSelfRegisteredAdmin
      ? 'simple'
      : additionalData?.accountMode || null,
    maxProperties: isSelfRegisteredAdmin
      ? 3
      : additionalData?.maxProperties || null,

    // Audit Fields
    createdAt: serverTimestamp() as unknown as BaseUser['createdAt'],
    updatedAt: serverTimestamp() as unknown as BaseUser['updatedAt'],
    createdBy: createdBy || null,
    lastLoginAt: serverTimestamp() as unknown as BaseUser['lastLoginAt'],

    // Profile Completion
    profileComplete: false, // Will be calculated
    profileCompletion: null, // Will be calculated
  }

  // Calculate profile completion
  const profileCompletion = calculateProfileCompletion(baseUserData)
  baseUserData.profileCompletion = profileCompletion
  baseUserData.profileComplete = profileCompletion.overall >= 80 // 80% threshold

  // Note: we deliberately don't call assertCorporateAccountIdNotSuperAdmin
  // here — it would do a getDoc on the proposed owner's user doc, but the
  // brand-new authenticated user does not have permission to read other
  // users' docs at this point. Ownership cascades from property →
  // invitation → user, and the upstream creation paths run the full guard
  // where they have privileges. See D-N-059.

  await setDoc(userRef, baseUserData as unknown as Record<string, unknown>)

  // Create userRelationships document for hierarchy support
  // Wrapped in try-catch to not fail registration if relationship setup fails
  // (relationships can be set up later by admin if needed)
  try {
    const relationshipRef = doc(db, 'userRelationships', user.uid)
    const relationshipData = {
      children: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }
    await setDoc(relationshipRef, relationshipData)
  } catch (relationshipError) {
    console.warn(
      '[createEnhancedUserDocument] Failed to create user relationships document:',
      relationshipError
    )
    // Continue - user doc was created successfully, relationships can be set up later
  }

  // The parent's children list is updated by onInvitationAccepted (server):
  // the rules refuse a new user writing the inviter's record (PB-67)

  return baseUserData
}

// Legacy user document creation (backward compatibility)
export const createUserDocument = async (
  user: User,
  role: UserRole,
  createdBy: string | null = null
) => {
  const { firstName, lastName } = extractNameFromDisplayNameOrEmail(
    user.displayName || '',
    user.email || ''
  )

  return createEnhancedUserDocument(
    user,
    role,
    { firstName, lastName },
    createdBy
  )
}

// Get current user data including role
export const getCurrentUserData = async (): Promise<UserData | null> => {
  const user = auth.currentUser

  if (!user) {
    return null
  }

  try {
    const userDoc = await getDoc(doc(db, 'users', user.uid))
    if (userDoc.exists()) {
      return { ...user, ...userDoc.data() } as UserData
    }
    return user as UserData
  } catch (error) {
    console.error('Error getting user data:', error)
    return user as UserData
  }
}

// Update user role
export const updateUserRole = async (
  uid: string,
  newRole: UserRole,
  updatedByUid: string
) => {
  try {
    const userRef = doc(db, 'users', uid)

    await updateDoc(userRef, {
      role: newRole,
      updatedAt: serverTimestamp(),
      updatedBy: updatedByUid,
    })

    return { success: true }
  } catch (error) {
    console.error('Error updating user role:', error)
    return { success: false, error: (error as Error).message }
  }
}

// Convert Admin to Corporate
export const convertAdminToCorporate = async (
  uid: string,
  updatedByUid: string
) => {
  return updateUserRole(uid, UserRoles.CORPORATE, updatedByUid)
}
